import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import {
    GraduationCap,
    MonitorPlay,
    Sparkles,
    Award,
    ArrowRight,
    ArrowDown,
    Quote,
    CheckCircle2,
    Send,
    Users,
    BookOpen,
    Target,
} from "lucide-react";
import { ESCUELAS } from "../data/escuelas";
import { fetchMaestrias, enviarContacto } from "../lib/api";
import { CALCULADORA_CONFIG } from "../config/calculadora.config";
import {
    CalculadoraProvider,
    useCalculadora,
    buildPlanMessage,
} from "../context/CalculadoraContext";
import { BlurText } from "../components/ui/BlurText";
import HLSVideo from "../components/ui/HLSVideo";
import CalculadoraWidget from "../components/CalculadoraWidget";
import MaestriaCard from "../components/MaestriaCard";
import FloatingCTA from "../components/FloatingCTA";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";
import { useReveal } from "../hooks/useReveal";
import { toast } from "sonner";

const ICON_MAP = {
    GraduationCap,
    MonitorPlay,
    Sparkles,
    Award,
    Users,
    BookOpen,
    Target,
};

function Reveal({ children, delay = 0, className = "" }) {
    const { ref, visible } = useReveal();
    return (
        <div
            ref={ref}
            style={{ transitionDelay: `${delay}ms` }}
            className={`transition-all duration-700 ease-out ${
                visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            } ${className}`}
        >
            {children}
        </div>
    );
}

export default function EscuelaLanding() {
    return (
        <CalculadoraProvider>
            <EscuelaLandingInner />
        </CalculadoraProvider>
    );
}

function EscuelaLandingInner() {
    const { slug } = useParams();
    const escuela = ESCUELAS[slug];
    const { snapshot } = useCalculadora();

    const [maestrias, setMaestrias] = useState([]);
    const [parallaxY, setParallaxY] = useState(0);
    const [contactForm, setContactForm] = useState({
        nombre: "",
        email: "",
        telefono: "",
        maestria_interes: "",
        mensaje: "",
    });
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (!escuela?.activa) return;
        fetchMaestrias(escuela.slugApi).then((res) => setMaestrias(res.data || []));
    }, [escuela]);

    useEffect(() => {
        const onScroll = () => setParallaxY(window.scrollY * 0.35);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    if (!escuela) return <Navigate to="/" replace />;

    if (!escuela.activa) {
        return (
            <div className="pt-32 pb-24 text-center max-w-2xl mx-auto px-6">
                <p className="text-xs uppercase tracking-[0.22em] font-bold text-unemi-orange-primary">
                    {escuela.nombreCorto}
                </p>
                <h1 className="font-heading text-4xl md:text-5xl text-unemi-blue-dark mt-3">
                    Próximamente
                </h1>
                <p className="mt-5 text-slate-600">
                    Estamos preparando esta landing. Vuelve pronto.
                </p>
                <Link
                    to="/educacion"
                    className="mt-8 inline-flex items-center gap-2 h-12 px-7 rounded-full bg-unemi-orange-primary text-white font-bold"
                >
                    Ver Escuela de Educación <ArrowRight className="w-4 h-4" />
                </Link>
            </div>
        );
    }

    const handleContactSubmit = async (e) => {
        e.preventDefault();
        if (!contactForm.nombre || !contactForm.email || !contactForm.mensaje) {
            toast.error("Completa los campos obligatorios.");
            return;
        }
        setSubmitting(true);

        // Construye el plan detallado desde el snapshot actual de la calculadora
        const planDetalle = buildPlanMessage(snapshot);
        const mensajeCompleto = planDetalle
            ? `${contactForm.mensaje}\n\n${planDetalle}`
            : contactForm.mensaje;

        try {
            await enviarContacto({
                ...contactForm,
                mensaje: mensajeCompleto,
                maestria_interes:
                    contactForm.maestria_interes || escuela.nombreCompleto,
                escuela: escuela.nombreCompleto,
            });
            toast.success("Mensaje enviado. Te contactaremos pronto.");
            setContactForm({ nombre: "", email: "", telefono: "", maestria_interes: "", mensaje: "" });
        } catch {
            toast.error("No pudimos enviar tu mensaje. Intenta nuevamente.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div data-testid={`escuela-landing-${escuela.slug}`} className="overflow-hidden">
            {/* ============ HERO ============ */}
            <section className="relative min-h-[1000px] flex flex-col justify-center bg-unemi-blue-dark text-white overflow-hidden pt-32 pb-20">
                <video
                    autoPlay
                    loop
                    muted
                    playsInline
                    className="absolute inset-0 w-full h-full object-cover z-0 opacity-50 mix-blend-screen"
                    src="https://cdn.jsdelivr.net/gh/hugosilvam3/VIDEOS@main/UNEMI.mp4"
                />
                <div className="absolute inset-0 bg-black/40 z-0" />
                <div className="absolute bottom-0 left-0 right-0 h-[300px] bg-gradient-to-b from-transparent to-unemi-blue-dark z-0 pointer-events-none" />

                <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28 w-full">
                    <Reveal>
                        <span className="inline-flex items-center gap-2 bg-white/10 border border-white/20 backdrop-blur-md px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-[0.22em] text-unemi-orange-light">
                            <span className="w-1.5 h-1.5 rounded-full bg-unemi-orange-primary animate-pulse" />
                            {escuela.eyebrow}
                        </span>
                    </Reveal>
                    <BlurText 
                        text={escuela.hero.titulo} 
                        delay={100}
                        className="font-heading italic text-5xl sm:text-6xl lg:text-[5.5rem] leading-[0.85] tracking-tight mt-6 max-w-4xl text-white"
                    />
                    <Reveal delay={300}>
                        <p className="mt-7 text-base md:text-xl text-slate-200/90 max-w-2xl leading-relaxed">
                            {escuela.hero.subtitulo}
                        </p>
                    </Reveal>
                    <Reveal delay={450}>
                        <div className="mt-10 flex flex-wrap gap-3">
                            <a
                                href="#calculadora"
                                data-testid="hero-cta-primario"
                                className="liquid-glass-strong inline-flex items-center gap-2 h-12 px-7 rounded-full text-white font-bold transition-all"
                            >
                                {escuela.hero.ctaPrimario}
                                <ArrowDown className="w-4 h-4" />
                            </a>
                            <a
                                href="#maestrias"
                                className="inline-flex items-center gap-2 h-12 px-7 rounded-full border-2 border-white/30 text-white font-bold hover:bg-white/10 transition-all"
                            >
                                {escuela.hero.ctaSecundario}
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={600}>
                        <div className="mt-20 grid grid-cols-3 gap-6 max-w-2xl border-t border-white/10 pt-10">
                            {escuela.hero.stats.map((s, i) => (
                                <div key={i}>
                                    <p className="font-heading text-3xl md:text-4xl font-medium text-white tabular-nums">
                                        {s.valor}
                                    </p>
                                    <p className="text-xs uppercase tracking-wider text-slate-400 mt-1 font-semibold">
                                        {s.label}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </Reveal>
                </div>

                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 text-xs uppercase tracking-[0.3em] font-bold flex flex-col items-center gap-2 animate-bounce">
                    Scroll
                    <ArrowDown className="w-4 h-4" />
                </div>
            </section>

            {/* ============ CALCULADORA ============ */}
            {CALCULADORA_CONFIG.enabled && (
                <section
                    id="calculadora"
                    data-testid="seccion-calculadora"
                    className="relative bg-unemi-blue-dark py-24 md:py-32 scroll-mt-20 overflow-hidden"
                >
                    {/* HLS Video Background */}
                    <div className="absolute inset-0 z-0 opacity-20 mix-blend-luminosity">
                        <HLSVideo 
                            src="https://stream.mux.com/9JXDljEVWYwWu01PUkAemafDugK89o01BR6zqJ3aS9u00A.m3u8"
                            className="w-full h-full object-cover"
                        />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-unemi-blue-dark via-transparent to-unemi-blue-dark opacity-90 z-0" />
                    
                    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-2 gap-14 items-start relative z-10">
                        <Reveal>
                            <div className="lg:sticky lg:top-28">
                            <p className="text-xs uppercase tracking-[0.22em] font-bold text-unemi-orange-primary">
                                Calculadora
                            </p>
                            <h2 className="font-heading text-4xl md:text-5xl text-white mt-3 leading-[1.05]">
                                Diseña tu plan de pago{" "}
                                <span className="italic text-unemi-orange-light">
                                    en tiempo real
                                </span>
                                .
                            </h2>
                            <p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed max-w-md">
                                Selecciona tu maestría, define cuota inicial y plazo, o
                                cambia a pago de contado para ver el descuento. Sin letras
                                chicas.
                            </p>
                            <ul className="mt-8 space-y-3">
                                {[
                                    "Cálculo dinámico desde el valor real del programa",
                                    "Plazo de 2 hasta 24 cuotas mensuales",
                                    "Descuentos por pago al contado disponibles",
                                ].map((t, i) => (
                                    <li key={i} className="flex items-start gap-2 text-white/80">
                                        <CheckCircle2 className="w-5 h-5 text-unemi-orange-primary mt-0.5 flex-shrink-0" />
                                        {t}
                                    </li>
                                ))}
                            </ul>
                            </div>
                        </Reveal>
                        <Reveal delay={200}>
                            <CalculadoraWidget maestrias={maestrias} variant="card" />
                        </Reveal>
                    </div>
                </section>
            )}

            {/* ============ MAESTRÍAS ============ */}
            <section
                id="maestrias"
                data-testid="seccion-maestrias"
                className="bg-transparent py-24 md:py-32 scroll-mt-20 relative"
            >
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <Reveal>
                        <p className="text-xs uppercase tracking-[0.22em] font-bold text-unemi-orange-primary">
                            Maestrías de la escuela
                        </p>
                        <h2 className="font-heading text-4xl md:text-5xl text-white mt-3 leading-[1.05] max-w-3xl">
                            Programas con inscripción abierta para 2026.
                        </h2>
                    </Reveal>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-14">
                        {maestrias.length === 0 ? (
                            <p className="col-span-full text-white/50 text-center py-12">
                                Cargando maestrías...
                            </p>
                        ) : (
                            maestrias.map((m, i) => (
                                <MaestriaCard key={m.pk} maestria={m} index={i} />
                            ))
                        )}
                    </div>
                </div>
            </section>

            {/* ============ BENEFICIOS ============ */}
            <section className="bg-unemi-blue-dark text-white py-24 md:py-32 relative overflow-hidden">
                <div className="absolute -top-20 -left-20 w-72 h-72 rounded-full bg-unemi-orange-primary/10 blur-3xl" />
                <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full bg-unemi-blue-light/20 blur-3xl" />
                <div className="max-w-7xl mx-auto px-6 md:px-10 relative">
                    <div className="grid md:grid-cols-2 gap-10 mb-16">
                        <Reveal>
                            <p className="text-xs uppercase tracking-[0.22em] font-bold text-unemi-orange-light">
                                {escuela.beneficios.eyebrow}
                            </p>
                            <h2 className="font-heading text-4xl md:text-5xl mt-3 leading-[1.05] max-w-xl">
                                {escuela.beneficios.titulo}
                            </h2>
                        </Reveal>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                        {escuela.beneficios.items.map((b, i) => {
                            const Icon = ICON_MAP[b.icon] || Sparkles;
                            return (
                                <Reveal key={i} delay={i * 100}>
                                    <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 h-full hover:bg-white/10 hover:-translate-y-1 transition-all">
                                        <span className="grid place-items-center w-12 h-12 rounded-xl bg-unemi-orange-primary/20 text-unemi-orange-light mb-5">
                                            <Icon className="w-5 h-5" />
                                        </span>
                                        <p className="font-heading text-lg text-white">
                                            {b.title}
                                        </p>
                                        <p className="text-sm text-slate-300 mt-2 leading-relaxed">
                                            {b.desc}
                                        </p>
                                    </div>
                                </Reveal>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* ============ METODOLOGÍA (timeline horizontal) ============ */}
            <section className="bg-transparent py-24 md:py-32 relative">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <div className="grid md:grid-cols-2 gap-10 mb-16">
                        <Reveal>
                            <p className="text-xs uppercase tracking-[0.22em] font-bold text-unemi-orange-primary">
                                {escuela.metodologia.eyebrow}
                            </p>
                            <h2 className="font-heading text-4xl md:text-5xl text-white mt-3 leading-[1.05]">
                                {escuela.metodologia.titulo}
                            </h2>
                        </Reveal>
                        <Reveal delay={150}>
                            <p className="text-white/70 leading-relaxed self-end">
                                {escuela.metodologia.descripcion}
                            </p>
                        </Reveal>
                    </div>
                    <div className="relative">
                        <div className="absolute top-7 left-0 right-0 h-px bg-gradient-to-r from-transparent via-unemi-orange-primary/40 to-transparent hidden md:block" />
                        <div className="grid grid-cols-2 md:grid-cols-5 gap-6">
                            {escuela.metodologia.pasos.map((p, i) => (
                                <Reveal key={i} delay={i * 100}>
                                    <div className="text-center md:text-left">
                                        <span className="inline-grid place-items-center w-14 h-14 rounded-full liquid-glass border border-unemi-orange-primary text-unemi-orange-primary font-heading text-lg font-semibold relative z-10">
                                            {p.num}
                                        </span>
                                        <p className="font-heading text-lg text-white mt-4">
                                            {p.title}
                                        </p>
                                        <p className="text-sm text-white/60 mt-1.5 leading-relaxed">
                                            {p.desc}
                                        </p>
                                    </div>
                                </Reveal>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* ============ TESTIMONIOS ============ */}
            <section className="bg-transparent py-24 md:py-32 relative">
                <div className="max-w-7xl mx-auto px-6 md:px-10">
                    <Reveal>
                        <p className="text-xs uppercase tracking-[0.22em] font-bold text-unemi-orange-primary">
                            Voces de egresados
                        </p>
                        <h2 className="font-heading text-4xl md:text-5xl text-white mt-3 leading-[1.05] max-w-3xl">
                            Historias reales de quienes ya lo lograron.
                        </h2>
                    </Reveal>
                    <div className="grid md:grid-cols-3 gap-6 mt-14">
                        {escuela.testimonios.map((t, i) => (
                            <Reveal key={i} delay={i * 120}>
                                <div className="liquid-glass border border-white/10 rounded-2xl p-7 h-full hover:shadow-[0_0_30px_rgba(252,126,0,0.1)] hover:-translate-y-1 transition-all">
                                    <Quote className="w-8 h-8 text-unemi-orange-primary/80" />
                                    <p className="mt-4 text-white/80 leading-relaxed italic">
                                        "{t.quote}"
                                    </p>
                                    <div className="mt-6 flex items-center gap-3 pt-5 border-t border-white/10">
                                        <img
                                            src={t.avatar}
                                            alt={t.nombre}
                                            className="w-11 h-11 rounded-full object-cover"
                                        />
                                        <div>
                                            <p className="font-bold text-sm text-white">
                                                {t.nombre}
                                            </p>
                                            <p className="text-xs text-white/50">{t.cargo}</p>
                                        </div>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>
                </div>
            </section>

            {/* ============ FAQ ============ */}
            <section className="bg-transparent py-24 md:py-32">
                <div className="max-w-3xl mx-auto px-6 md:px-10">
                    <Reveal>
                        <p className="text-xs uppercase tracking-[0.22em] font-bold text-unemi-orange-primary">
                            Preguntas frecuentes
                        </p>
                        <h2 className="font-heading text-4xl md:text-5xl text-white mt-3 mb-12">
                            Resolvemos tus dudas.
                        </h2>
                    </Reveal>
                    <Accordion type="single" collapsible className="space-y-3">
                        {escuela.faq.map((f, i) => (
                            <AccordionItem
                                key={i}
                                value={`faq-${i}`}
                                data-testid={`faq-${i}`}
                                className="liquid-glass border border-white/10 rounded-2xl px-6 data-[state=open]:border-unemi-orange-primary/30 transition-all mb-3"
                            >
                                <AccordionTrigger className="font-heading text-base text-left text-white hover:no-underline">
                                    {f.q}
                                </AccordionTrigger>
                                <AccordionContent className="text-white/70 leading-relaxed">
                                    {f.a}
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                </div>
            </section>

            {/* ============ CONTACTO INLINE ============ */}
            <section
                id="contacto"
                className="bg-unemi-blue-dark text-white py-24 md:py-32 relative overflow-hidden"
            >
                <div className="absolute -bottom-20 -right-20 w-96 h-96 rounded-full bg-unemi-orange-primary/20 blur-3xl" />
                <div className="max-w-5xl mx-auto px-6 md:px-10 relative">
                    <div className="grid md:grid-cols-2 gap-14 items-start">
                        <Reveal>
                            <p className="text-xs uppercase tracking-[0.22em] font-bold text-unemi-orange-light">
                                ¿Listo para empezar?
                            </p>
                            <h2 className="font-heading text-4xl md:text-5xl mt-3 leading-[1.05]">
                                Hablemos sobre tu próximo paso.
                            </h2>
                            <p className="mt-5 text-slate-300 leading-relaxed max-w-md">
                                Déjanos tus datos y un asesor de admisiones de la{" "}
                                {escuela.nombreCompleto} te contactará en menos de 24 horas.
                            </p>
                        </Reveal>
                        <Reveal delay={150}>
                            <form
                                data-testid="form-contacto-escuela"
                                onSubmit={handleContactSubmit}
                                className="bg-white border border-slate-200 rounded-3xl p-7 md:p-9 space-y-4 shadow-2xl shadow-black/20"
                            >
                                <div>
                                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">
                                        Nombre completo *
                                    </Label>
                                    <Input
                                        data-testid="contacto-nombre"
                                        value={contactForm.nombre}
                                        onChange={(e) =>
                                            setContactForm({ ...contactForm, nombre: e.target.value })
                                        }
                                        className="h-12 bg-slate-50 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus-visible:ring-unemi-orange-primary"
                                        required
                                    />
                                </div>
                                <div className="grid sm:grid-cols-2 gap-4">
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">
                                            Email *
                                        </Label>
                                        <Input
                                            data-testid="contacto-email"
                                            type="email"
                                            value={contactForm.email}
                                            onChange={(e) =>
                                                setContactForm({ ...contactForm, email: e.target.value })
                                            }
                                            className="h-12 bg-slate-50 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus-visible:ring-unemi-orange-primary"
                                            required
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">
                                            Teléfono
                                        </Label>
                                        <Input
                                            data-testid="contacto-telefono"
                                            value={contactForm.telefono}
                                            onChange={(e) =>
                                                setContactForm({
                                                    ...contactForm,
                                                    telefono: e.target.value,
                                                })
                                            }
                                            className="h-12 bg-slate-50 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus-visible:ring-unemi-orange-primary"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">
                                        Maestría de interés *
                                    </Label>
                                    <Select
                                        value={contactForm.maestria_interes}
                                        onValueChange={(v) =>
                                            setContactForm({ ...contactForm, maestria_interes: v })
                                        }
                                    >
                                        <SelectTrigger
                                            data-testid="contacto-maestria"
                                            className="h-12 bg-slate-50 border-slate-200 rounded-xl text-slate-800 focus:ring-unemi-orange-primary"
                                        >
                                            <SelectValue placeholder="Selecciona la maestría" />
                                        </SelectTrigger>
                                        <SelectContent className="max-w-[--radix-select-trigger-width]">
                                            {maestrias.map((m) => (
                                                <SelectItem key={m.pk} value={m.nombre_maestria}>
                                                    <span className="block truncate">
                                                        {m.nombre_maestria}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="text-xs uppercase tracking-wider font-semibold text-slate-500 mb-2 block">
                                        Mensaje *
                                    </Label>
                                    <Textarea
                                        data-testid="contacto-mensaje"
                                        rows={4}
                                        value={contactForm.mensaje}
                                        onChange={(e) =>
                                            setContactForm({
                                                ...contactForm,
                                                mensaje: e.target.value,
                                            })
                                        }
                                        className="bg-slate-50 border-slate-200 rounded-xl text-slate-800 placeholder-slate-400 focus-visible:ring-unemi-orange-primary"
                                        required
                                    />
                                </div>
                                <button
                                    type="submit"
                                    data-testid="contacto-submit"
                                    disabled={submitting}
                                    className="bg-unemi-orange-primary hover:bg-orange-600 w-full h-12 inline-flex items-center justify-center gap-2 rounded-full text-white font-bold transition-all disabled:opacity-60"
                                >
                                    {submitting ? "Enviando..." : "Quiero información"}
                                    <Send className="w-4 h-4" />
                                </button>
                            </form>
                        </Reveal>
                    </div>
                </div>
            </section>

            {/* Floating CTA */}
            <FloatingCTA />
        </div>
    );
}
